import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Trash2, RotateCcw, Users, Building2, Search, Bot } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { apiService, ElementCorbeille } from '@/services/api'

/* La corbeille des contacts et des entreprises (28/10/2026), réservée aux
   administrateurs. Elle ne se vide qu'à la main, sans durée limite. */

type Filtre = 'tout' | 'contact' | 'entreprise'

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export default function Corbeille() {
  const estAdmin = Boolean(useAuthStore((etat) => etat.user?.is_admin))
  const { toast } = useToast()
  const [elements, setElements] = useState<ElementCorbeille[]>([])
  const [total, setTotal] = useState(0)
  const [chargement, setChargement] = useState(true)
  const [filtre, setFiltre] = useState<Filtre>('tout')
  const [recherche, setRecherche] = useState('')
  const [enCours, setEnCours] = useState<number | null>(null)
  const [aSupprimer, setASupprimer] = useState<ElementCorbeille | null>(null)
  const [viderOuvert, setViderOuvert] = useState(false)

  const charger = useCallback(async () => {
    setChargement(true)
    const r = await apiService.getCorbeille(filtre === 'tout' ? undefined : filtre, recherche)
    if (r.success && r.data) {
      setElements(r.data.elements)
      setTotal(r.data.total)
    } else {
      toast({ title: 'La corbeille n’a pas pu être chargée', description: r.error, variant: 'destructive' })
    }
    setChargement(false)
  }, [filtre, recherche, toast])

  useEffect(() => {
    const minuteur = setTimeout(() => void charger(), 250)
    return () => clearTimeout(minuteur)
  }, [charger])

  if (!estAdmin) return <Navigate to="/contacts" />

  const restaurer = async (el: ElementCorbeille) => {
    setEnCours(el.id)
    const r = await apiService.restaurerDeLaCorbeille(el.id)
    setEnCours(null)
    if (r.success && r.data) {
      const quoi = el.type === 'contact' ? 'Le contact' : 'L’entreprise'
      toast({
        title: `${quoi} « ${el.nom} » est restauré${el.type === 'entreprise' ? 'e' : ''}`,
        description: r.data.remarques.length ? r.data.remarques.join(' ') : 'La fiche est revenue à l’identique, sous son numéro d’origine.',
        variant: 'success',
      })
      await charger()
    } else {
      toast({ title: 'La restauration a échoué', description: r.error, variant: 'destructive' })
    }
  }

  const supprimer = async () => {
    if (!aSupprimer) return
    setEnCours(aSupprimer.id)
    const r = await apiService.supprimerDeLaCorbeille(aSupprimer.id)
    setEnCours(null)
    setASupprimer(null)
    if (r.success) {
      toast({ title: `« ${aSupprimer.nom} » est supprimé définitivement`, variant: 'success' })
      await charger()
    } else {
      toast({ title: 'La suppression a échoué', description: r.error, variant: 'destructive' })
    }
  }

  const vider = async () => {
    setViderOuvert(false)
    const r = await apiService.viderLaCorbeille()
    if (r.success) {
      toast({ title: 'La corbeille est vide', description: `${r.data?.supprimes ?? 0} fiche(s) supprimée(s) définitivement.`, variant: 'success' })
      await charger()
    } else {
      toast({ title: 'La corbeille n’a pas pu être vidée', description: r.error, variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
          <Trash2 className="w-6 h-6 text-blue-600" />
          <span>Corbeille</span>
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Les contacts et les entreprises supprimés, depuis l’interface ou par le connecteur IA. Ils restent ici jusqu’à ce qu’un administrateur les restaure ou les supprime définitivement.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>{total === 0 ? 'La corbeille est vide' : `${total} fiche${total > 1 ? 's' : ''} dans la corbeille`}</CardTitle>
            <CardDescription>
              Restaurer une fiche la remet à sa place, sous son numéro d’origine, avec ses champs personnalisés. Une entreprise restaurée retrouve ceux de ses anciens contacts qui n’ont pas changé d’entreprise entre-temps.
            </CardDescription>
          </div>
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 shrink-0" disabled={total === 0} onClick={() => setViderOuvert(true)}>
            <Trash2 className="w-4 h-4 mr-2" />Vider la corbeille
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {(['tout', 'contact', 'entreprise'] as Filtre[]).map((f) => (
              <Button key={f} size="sm" variant={filtre === f ? 'default' : 'outline'} onClick={() => setFiltre(f)}>
                {f === 'tout' ? 'Tout' : f === 'contact' ? 'Contacts' : 'Entreprises'}
              </Button>
            ))}
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input className="pl-9" placeholder="Rechercher un nom, une adresse email…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            </div>
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>FICHE</TableHead>
                  <TableHead>SUPPRIMÉE LE</TableHead>
                  <TableHead>PAR</TableHead>
                  <TableHead className="text-right">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {chargement ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">Chargement…</TableCell></TableRow>
                ) : elements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      {recherche || filtre !== 'tout' ? 'Aucune fiche ne correspond à votre recherche.' : 'Aucune fiche n’a été supprimée.'}
                    </TableCell>
                  </TableRow>
                ) : elements.map((el) => (
                  <TableRow key={el.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {el.type === 'contact'
                          ? <Users className="w-4 h-4 text-gray-400 shrink-0" />
                          : <Building2 className="w-4 h-4 text-gray-400 shrink-0" />}
                        <div>
                          <div className="font-medium">{el.nom}</div>
                          {el.detail && <div className="text-xs text-gray-500">{el.detail}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(el.supprime_le)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{el.supprime_par ?? 'inconnu'}</span>
                        {el.origine === 'connecteur' && (
                          <Badge variant="outline" className="border-purple-300 text-purple-700" title="Supprimée par le connecteur IA, au nom de cette personne">
                            <Bot className="w-3 h-3 mr-1" />Connecteur IA
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-2" disabled={enCours === el.id} onClick={() => void restaurer(el)}>
                        <RotateCcw className="w-4 h-4 mr-1" />Restaurer
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" disabled={enCours === el.id} onClick={() => setASupprimer(el)} title="Supprimer définitivement">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(aSupprimer)} onOpenChange={(o) => !o && setASupprimer(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer définitivement</DialogTitle>
            <DialogDescription>
              « {aSupprimer?.nom} » sera effacé pour de bon, sans retour possible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setASupprimer(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => void supprimer()}>Supprimer définitivement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viderOuvert} onOpenChange={setViderOuvert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vider la corbeille</DialogTitle>
            <DialogDescription>
              Les {total} fiches de la corbeille seront effacées pour de bon, sans retour possible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViderOuvert(false)}>Annuler</Button>
            <Button variant="destructive" onClick={() => void vider()}>Vider la corbeille</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
